import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { LuHandshake, LuPen, LuPercent, LuPiggyBank } from 'react-icons/lu'
import { getPlaceSuggestions } from '../services/placesService.js'

const DEFAULT_RADIUS = '10'
const SUGGESTION_RADIUS = '3'

const SearchHero = ({ onSearch }) => {
  const [locationInput, setLocationInput] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [suggestionsVisible, setSuggestionsVisible] = useState(false)
  const [confirmedLocation, setConfirmedLocation] = useState('')
  const [locationError, setLocationError] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const containerRef = useRef(null)
  const fetchRef = useRef(0)
  const suppressSuggestionsRef = useRef(false)

  const runSearch = (locationValue, radius = DEFAULT_RADIUS) => {
    if (!locationValue) return
    setLocationError('')
    onSearch?.({ location: locationValue, radius })
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    if (!confirmedLocation) {
      setLocationError('Select a location from the list.')
      return
    }
    runSearch(confirmedLocation)
  }

  useEffect(() => {
    if (suppressSuggestionsRef.current) {
      suppressSuggestionsRef.current = false
      return
    }
    if (!locationInput || locationInput.trim().length < 2) {
      setSuggestions([])
      setSuggestionsVisible(false)
      return
    }
    const fetchId = ++fetchRef.current
    const timeout = setTimeout(() => {
      const snapshot = fetchId
      getPlaceSuggestions(locationInput.trim(), { limit: 6 })
        .then((items) => {
          if (snapshot !== fetchRef.current) return
          setSuggestions(items)
          setSuggestionsVisible(items.length > 0)
        })
        .catch(() => {
          if (snapshot !== fetchRef.current) return
          setSuggestions([])
          setSuggestionsVisible(false)
        })
    }, 200)
    return () => clearTimeout(timeout)
  }, [locationInput])

  useEffect(() => {
    setHighlightedIndex(-1)
  }, [suggestions])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!containerRef.current) return
      if (!containerRef.current.contains(event.target)) {
        setSuggestionsVisible(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const cancelPendingSuggestions = () => {
    fetchRef.current += 1
  }

  const handleSuggestionSelect = (suggestion) => {
    cancelPendingSuggestions()
    suppressSuggestionsRef.current = true
    setLocationInput(suggestion.value)
    setConfirmedLocation(suggestion.value)
    setSuggestions([])
    setSuggestionsVisible(false)
    setLocationError('')
    setHighlightedIndex(-1)
    runSearch(suggestion.value, SUGGESTION_RADIUS)
  }

  const handleInputKeyDown = (event) => {
    if (!suggestions.length) {
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setSuggestionsVisible(true)
      setHighlightedIndex((prev) => {
        if (prev < 0) return 0
        return (prev + 1) % suggestions.length
      })
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setSuggestionsVisible(true)
      setHighlightedIndex((prev) => {
        if (prev < 0) return suggestions.length - 1
        return (prev - 1 + suggestions.length) % suggestions.length
      })
      return
    }
    if (event.key === 'Enter') {
      if (!suggestionsVisible) return
      const selected =
        highlightedIndex >= 0 ? suggestions[highlightedIndex] : suggestions.length === 1 ? suggestions[0] : null
      if (selected) {
        event.preventDefault()
        handleSuggestionSelect(selected)
      }
      return
    }
    if (event.key === 'Escape') {
      setSuggestionsVisible(false)
      setHighlightedIndex(-1)
    }
  }

  return (
    <section className="search-hero" aria-label="Primary search">
      <Link to="/sell" className="search-hero__list-cta primary-button">
        <LuPen aria-hidden="true" /> List your home for free
      </Link>
      <div className="search-hero__content">
        <p className="search-hero__eyebrow">Owner-to-buyer</p>
        <h1>
          Meet serious buyers
          <br />
          and sellers directly
        </h1>
        <p>
          Browse private listings, message real homeowners, and agree a viewing without an estate agent in sight. It’s
          AutoTrader-style matchmaking for property people.
        </p>

        <div className="graphic-row">
          <span className="graphic-node">Seller</span>


          <span className="graphic-arrow" aria-hidden="true">
            ←→
          </span>
          <span className="graphic-node">Buyer</span>      <p>No middlemen. No commission. No Nonsense.</p>
        </div>


      </div>
      <form className="search-hero__form" onSubmit={handleSubmit}>
        <label className="search-hero__single location-autocomplete" ref={containerRef}>
          <input
            type="text"
            name="location"
            placeholder="e.g. SW3 or Brighton"
            value={locationInput}
            onChange={(event) => {
              cancelPendingSuggestions()
              setLocationInput(event.target.value)
              setConfirmedLocation('')
              setLocationError('')
            }}
            required
            onFocus={() => setSuggestionsVisible(suggestions.length > 0)}
            onKeyDown={handleInputKeyDown}
          />
          {suggestionsVisible && suggestions.length > 0 && (
            <ul className="location-suggestions">
              {suggestions.map((suggestion, index) => (
                <li
                  key={`${suggestion.type}-${suggestion.value}`}
                  onMouseDown={() => handleSuggestionSelect(suggestion)}
                  className={highlightedIndex === index ? 'is-highlighted' : ''}
                >
                  <span className="location-suggestions__label">{suggestion.label}</span>
                  {suggestion.type === 'postcode' && suggestion.city && (
                    <span className="location-suggestions__meta">{suggestion.city}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {locationError && <small className="error-text">{locationError}</small>}
        </label>

        <div className="search-hero__controls">
          <button type="submit" className="primary-button">
            Search listings
          </button>
        </div>
      </form>

      <div className="search-hero__stats">
        <div className="stat-pill">
          <LuPercent aria-hidden="true" />
          <div>
            <strong style={{ marginRight: '4px' }}>0%</strong>
            Buyer commission
          </div>
        </div>
        <div className="stat-pill">
          <LuHandshake aria-hidden="true" />
          <div>
            <strong style={{ marginRight: '4px' }}>0%</strong>
            Seller commission
          </div>
        </div>
        <div className="stat-pill">
          <LuPiggyBank aria-hidden="true" />
          <div>
            <strong style={{ marginRight: '4px' }}>£0</strong>
            Platform fees
          </div>
        </div>
      </div>
    </section>
  )
}

export default SearchHero
